<template>
  <div
    class="flex justify-between items-center mt-4 p-sm-8 p-5 pt-0"
    :class="totalPages > 1 && totalItems > itemsPerPage ? 'flex-col sm:flex-row' : 'flex-row'"
  >
    <!--left-->
    <span class="text-gray-600 text-sm font-medium inline-flex !flex-row whitespace-nowrap flex-wrap mb-3 sm:mb-0">
      Showing
      <span class="flex mx-1">
        (
        <span class="text-black block">
          {{ itemsShowing }}
        </span>
        )
      </span>
      of
      <span class="text-black block mx-1">
        {{ totalItems }}
      </span>
      Results
    </span>

    <!--right-->
    <div v-if="totalPages > 1 && totalItems > itemsPerPage">
      <ul
        class="pagination items-center justify-center sm:justify-end list-none m-0"
        style="width:min-content; text-wrap: nowrap;"
      >
        <!-- First page -->
        <li>
          <a
            :class="{ 'pointer-events-none': isFirstPage }"
            @click="goToPage(1)"
          >
            <FontAwesomeIcon
              :class="{ 'bg-gray-200': isFirstPage }"
              :icon="faChevronDoubleLeft"
            />
          </a>
        </li>

        <!-- Previous page -->
        <li>
          <a
            :class="{ 'pointer-events-none': isFirstPage }"
            @click="goToPage(currentPage - 1)"
          >
            <FontAwesomeIcon
              :class="{ 'bg-gray-200': isFirstPage }"
              :icon="faChevronLeft"
            />
          </a>
        </li>

        <!--Input Search-->
        <li class="h-full">
          <input
            v-model="inputPage"
            type="text"
            class="@keydown.enter.stop=&quot;handleEnter&quot;"
            @keydown.stop="handleInput"
            @keydown.enter="goToPage(inputPage)"
          >
        </li>

        <li class="me-1">
          of {{ totalPages }}
        </li>

        <!-- Next page -->
        <li>
          <a
            :class="{ 'pointer-events-none': isLastPage }"
            @click="goToPage(currentPage + 1)"
          >
            <FontAwesomeIcon
              :class="{ 'bg-gray-200': isLastPage }"
              :icon="faChevronRight"
            />
          </a>
        </li>

        <!-- Last page -->
        <li>
          <a
            :class="{ 'pointer-events-none': isLastPage }"
            @click="goToPage(totalPages)"
          >
            <FontAwesomeIcon
              :class="{ 'bg-gray-200': isLastPage }"
              :icon="faChevronDoubleRight"
            />
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

import {
  faChevronDoubleLeft,
  faChevronDoubleRight,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRouter } from 'vue-router';

const router = useRouter();

const emit = defineEmits(['change']);

const props = defineProps({
  itemsPerPage: {
    type: Number,
    default: 10,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
  targetId: {
    type: String,
    default: null,
  },
});

const inputPage = ref(props.currentPage);
const totalPages = computed(() => (props.totalItems && props.itemsPerPage) ? Math.ceil(props.totalItems / props.itemsPerPage) : 0);
const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === totalPages.value);
const itemsShowing = computed(() => {
  if (props.totalItems === 0) return '0';

  const start = (props.currentPage - 1) * props.itemsPerPage + 1;
  const end = Math.min(props.currentPage * props.itemsPerPage, props.totalItems);

  return `${start} - ${end}`;
});

const handleInput = event => {
  if (event.key.length === 1 && isNaN(Number(event.key))) {
    event.preventDefault();
    event.stopPropagation();
  }
};

const goToPage = async (page) => {
  inputPage.value = page;
  emit('change', page);

  await router.push({ hash: `#${props.targetId}` });
};

</script>

<style lang="css" scoped>
a {
  cursor: pointer;
}

</style>
